
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Settings, Menu, X, ChevronRight, Landmark } from 'lucide-react';
import { User, UserRole } from './types';
import Dashboard from './components/Dashboard';
import MeetingRoom from './components/MeetingRoom';
import Voters from './components/Voters';
import Archive from './components/Archive';
import SettingsView from './components/Settings';
import Login from './components/Login';

const SMSALogo = ({ className }: { className?: string }) => {
  const customLogo = localStorage.getItem('smsa_custom_logo');
  if (customLogo) {
    return <img src={customLogo} alt="Logo" className={className} />;
  }
  return (
    <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 5C25.147 5 5 25.147 5 50C5 74.853 25.147 95 50 95C74.853 95 95 74.853 95 50C95 25.147 74.853 5 50 5ZM50 12C70.987 12 88 29.013 88 50C88 70.987 70.987 88 50 88C29.013 88 12 70.987 12 50C12 29.013 29.013 12 50 12Z" fill="currentColor"/>
      <path d="M35 70V45H40V70H35ZM47.5 70V45H52.5V70H47.5ZM60 70V45H65V70H60ZM30 40C30 30 50 20 70 40H30Z" fill="currentColor"/>
    </svg>
  );
};

const NavItem = ({ icon, label, to, expanded }: { icon: React.ReactNode, label: string, to: string, expanded: boolean }) => {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
        isActive
          ? 'bg-indigo-50 text-indigo-600'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
      }`}
    >
      <span className={`${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`}>
        {icon}
      </span>
      {expanded && <span className="font-bold text-sm truncate">{label}</span>}
      {expanded && isActive && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-600" />
      )}
    </Link>
  );
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('smsa_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogin = (u: User) => {
    setUser(u);
    localStorage.setItem('smsa_user', JSON.stringify(u));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('smsa_user');
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdmin = [UserRole.SYSTEM_ADMIN, UserRole.PRESIDENT, UserRole.NATIONAL_OFFICER, UserRole.LOCAL_DIRECTOR].includes(user.role);

  return (
    <HashRouter>
      <div className="flex flex-row h-screen w-full min-h-screen bg-[#f8fafc] text-slate-900 font-sans overflow-hidden">
        <aside 
          className={`${sidebarOpen ? 'w-72' : 'w-20'} bg-white border-r border-slate-200 transition-all duration-300 flex flex-col z-30 shadow-sm shrink-0 h-full`}
        >
          <div className="p-5 flex items-center gap-3 border-b border-slate-100 min-h-[80px]">
            <div className="bg-white p-1 rounded-xl shadow-sm shrink-0 border border-slate-100 flex items-center justify-center w-12 h-12">
              <SMSALogo className="text-indigo-600 w-10 h-10 object-contain" />
            </div>
            {sidebarOpen && (
              <div className="overflow-hidden">
                <h1 className="font-black text-xl tracking-tighter text-indigo-600 whitespace-nowrap uppercase leading-none">SMSA-Votefix</h1>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-1 text-nowrap truncate">{user.role.replace('_', ' ')}</p>
              </div>
            )}
          </div>

          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            <div className={`text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 mt-2 px-3 ${!sidebarOpen && 'text-center'}`}>
              {sidebarOpen ? 'Main Portal' : '•'}
            </div>
            <NavItem icon={<LayoutDashboard size={20} />} label="Dashboard" to="/" expanded={sidebarOpen} />
            
            {isAdmin && (
              <NavItem icon={<Users size={20} />} label="Delegates" to="/voters" expanded={sidebarOpen} />
            )}
            
            <NavItem icon={<FileText size={20} />} label="Archive" to="/archive" expanded={sidebarOpen} />
            <NavItem icon={<Settings size={20} />} label="Portal Config" to="/settings" expanded={sidebarOpen} />
          </nav>

          <div className="p-4 mt-auto border-t border-slate-100">
            {sidebarOpen && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-white text-[10px] shrink-0">
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate leading-tight">{user.name}</p>
                    <button onClick={handleLogout} className="text-[10px] font-black text-rose-500 uppercase hover:underline">Exit Portal</button>
                  </div>
                </div>
              </div>
            )}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex items-center gap-3 w-full p-3 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-900 transition-all group"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={20} className="mx-auto" />}
              {sidebarOpen && <span className="font-bold text-sm">Collapse Sidebar</span>}
            </button>
          </div>
        </aside>

        <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-[#f8fafc]">
          <div className="flex-1 overflow-y-auto">
            <Routes>
              <Route path="/" element={<Dashboard user={user} />} />
              <Route path="/voters" element={isAdmin ? <Voters user={user} /> : <Navigate to="/" />} />
              <Route path="/meeting/:id" element={<MeetingRoom />} />
              <Route path="/archive" element={<Archive />} />
              <Route path="/settings" element={<SettingsView onUpdateUser={handleLogin} />} />
            </Routes>
          </div>
        </main>
      </div>
    </HashRouter>
  );
};

export default App;
